import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    BriefcaseIcon,
    DocumentDuplicateIcon,
    UserGroupIcon,
    UserPlusIcon,
    MagnifyingGlassIcon,
    PlusIcon,
    XMarkIcon,
    ChevronRightIcon,
    CogIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';

// --- START: MOCK DATA ---
const mockJobs = [
    { id: 1, reqId: 'REQ-2024-001', title: 'Lead Software Engineer', department: 'Technology', applicants: 85, status: 'In Progress', date: '2024-01-15', positions: 2, description: 'Looking for an experienced lead software engineer to guide our development team.', attachingLink: 'https://company.com/job-details/lead-engineer' },
    { id: 2, reqId: 'REQ-2024-002', title: 'Senior UX Designer', department: 'Product', applicants: 120, status: 'Hold', date: '2024-01-10', positions: 1, description: 'Seeking a creative UX designer to enhance user experience across our products.', attachingLink: 'https://company.com/job-details/ux-designer' },
    { id: 3, reqId: 'REQ-2024-003', title: 'HR Manager', department: 'Human Resources', applicants: 45, status: 'Closed', date: '2024-01-08', positions: 1, description: 'HR Manager to oversee recruitment and employee relations.', attachingLink: '' },
    { id: 4, reqId: 'REQ-2024-004', title: 'Data Analyst', department: 'Technology', applicants: 150, status: 'In Progress', date: '2024-01-12', positions: 3, description: 'Data analyst to help drive business insights through data analysis.', attachingLink: 'https://company.com/job-details/data-analyst' },
];
// --- END: MOCK DATA ---

// Modal for adding a new job - Cleaned up to look like a modern Keka/Zoho dialog
const AddJobModal = ({ isOpen, onClose, onSave }) => {
    const [jobTitle, setJobTitle] = useState('');
    const [jobDepartment, setJobDepartment] = useState('');
    const [jobDescription, setJobDescription] = useState('');
    const [positions, setPositions] = useState(1);
    const [attachingLink, setAttachingLink] = useState('');

    const handleSave = () => {
        if (jobTitle && jobDepartment && jobDescription) {
            onSave({ 
                title: jobTitle, 
                department: jobDepartment, 
                description: jobDescription,
                positions: positions,
                attachingLink: attachingLink
            });
            setJobTitle('');
            setJobDepartment('');
            setJobDescription('');
            setPositions(1);
            setAttachingLink('');
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-30 backdrop-blur-sm p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, y: -20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className="bg-white p-6 md:p-8 rounded-2xl shadow-2xl w-full max-w-md mx-auto"
                    >
                        {/* Modal Header */}
                        <div className="flex justify-between items-center pb-4 border-b border-gray-100 mb-6">
                            <h2 className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
                                Add New Job Opening
                            </h2>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-50"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                        
                        {/* Modal Body */}
                        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Job Title</label>
                                    <input
                                        type="text"
                                        value={jobTitle}
                                        onChange={(e) => setJobTitle(e.target.value)}
                                        placeholder="e.g., Senior Software Engineer"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Department</label>
                                    <input
                                        type="text"
                                        value={jobDepartment}
                                        onChange={(e) => setJobDepartment(e.target.value)}
                                        placeholder="e.g., Technology"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Number of Positions</label>
                                    <input
                                        type="number"
                                        value={positions}
                                        onChange={(e) => setPositions(parseInt(e.target.value) || 1)}
                                        min="1"
                                        placeholder="1"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Attaching Link</label>
                                    <input
                                        type="url"
                                        value={attachingLink}
                                        onChange={(e) => setAttachingLink(e.target.value)}
                                        placeholder="https://example.com/job-details"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Job Description</label>
                                    <textarea
                                        value={jobDescription}
                                        onChange={(e) => setJobDescription(e.target.value)}
                                        placeholder="Describe the job role, responsibilities, and requirements..."
                                        rows={4}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow transition-colors resize-none"
                                    />
                                </div>
                            </div>
                        </form>

                        {/* Modal Footer */}
                        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100 mt-6">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2 rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                onClick={handleSave}
                                className="px-5 py-2 rounded-lg bg-indigo-600 text-white shadow-md shadow-indigo-200 hover:bg-indigo-700 transition-colors text-sm font-medium"
                            >
                                <PlusIcon className="h-4 w-4 inline mr-1" /> Save Job
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

// Main Dashboard Component
const RecruitmentDashboard = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState(mockJobs);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddJobModalOpen, setIsAddJobModalOpen] = useState(false);

    // Dashboard Metrics
    const totalOpenings = jobs.length;
    const totalApplicants = jobs.reduce((acc, job) => acc + job.applicants, 0);
    const offersExtended = jobs.filter(job => job.status === 'Closed').length;
    const newApplications = 25; // Mock data for today's new applications

    const handleAddJob = ({ title, department, description, positions, attachingLink }) => {
        const currentYear = new Date().getFullYear();
        const reqNumber = String(jobs.length + 1).padStart(3, '0');
        const reqId = `REQ-${currentYear}-${reqNumber}`;
        
        const newJob = {
            id: jobs.length + 1,
            reqId,
            title,
            department,
            description,
            positions,
            attachingLink,
            applicants: 0,
            status: 'In Progress',
            date: new Date().toISOString().split('T')[0],
        };
        setJobs([newJob, ...jobs]);
        setIsAddJobModalOpen(false);
    };

    // Job filtering logic
    const filteredJobs = useMemo(() => {
        if (!searchTerm) {
            return jobs;
        }
        const lowerCaseSearch = searchTerm.toLowerCase();
        return jobs.filter(job =>
            job.title.toLowerCase().includes(lowerCaseSearch) ||
            job.department.toLowerCase().includes(lowerCaseSearch) ||
            job.status.toLowerCase().includes(lowerCaseSearch)
        );
    }, [searchTerm, jobs]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'In Progress': return 'bg-blue-100 text-blue-700 ring-blue-300';
            case 'Hold': return 'bg-yellow-100 text-yellow-700 ring-yellow-300';
            case 'Closed': return 'bg-gray-100 text-gray-700 ring-gray-300';
            default: return 'bg-gray-100 text-gray-700 ring-gray-300';
        }
    };

    const handleJobDetails = (job) => {
        alert(`Job Details:\n\nTitle: ${job.title}\nDepartment: ${job.department}\nPositions: ${job.positions}\nDate Posted: ${job.date}\nApplicants: ${job.applicants}\nStatus: ${job.status}\n\nDescription:\n${job.description}`);
    };

    const handleStatusChange = (jobId, newStatus) => {
        setJobs(jobs.map(job => 
            job.id === jobId ? { ...job, status: newStatus } : job
        ));
    };

    const handleDeleteJob = (jobId) => {
        if (window.confirm('Are you sure you want to delete this job posting?')) {
            setJobs(jobs.filter(job => job.id !== jobId));
        }
    };

    const handleReqIdClick = (reqId) => {
        const job = jobs.find(j => j.reqId === reqId);
        const jobData = {
            reqId: job.reqId,
            title: job.title,
            department: job.department,
            positions: job.positions,
            description: job.description,
            attachingLink: job.attachingLink || ''
        };
        navigate('/admin/interview-rounds-setup', { state: { jobData } });
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 20 } },
    };
    
    // We add an inline style block to import and set the custom fonts
    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-8" style={{ fontFamily: 'Roboto, sans-serif' }}>
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Roboto:wght@400;500;700&display=swap');
                    .font-poppins { font-family: 'Poppins', sans-serif; }
                    .font-roboto { font-family: 'Roboto', sans-serif; }
                `}
            </style>

            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="max-w-7xl mx-auto mb-10"
            >
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-poppins">
                            Job Openings
                        </h1>
                        <p className="text-lg text-gray-500 mt-1 font-roboto">
                            Real-time insights for your hiring pipeline.
                        </p>
                    </div>
                </div>
            </motion.div>

            {/* Dashboard Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10 max-w-7xl mx-auto">
                {[
                    { icon: BriefcaseIcon, color: 'blue', value: totalOpenings, label: 'Open Jobs' },
                    { icon: DocumentDuplicateIcon, color: 'indigo', value: totalApplicants, label: 'Total Applicants' },
                    { icon: UserGroupIcon, color: 'purple', value: newApplications, label: 'New Today' },
                    { icon: UserPlusIcon, color: 'green', value: offersExtended, label: 'Offers Extended' },
                ].map((item, index) => (
                    <motion.div key={index} variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: index * 0.1 }}>
                        <div className={`bg-white p-6 rounded-2xl shadow-lg border border-gray-100 flex items-center space-x-4 transition-all hover:shadow-xl hover:scale-[1.02] transform`}>
                            <div className={`p-3 rounded-xl bg-${item.color}-100`}>
                                <item.icon className={`h-8 w-8 text-${item.color}-600`} />
                            </div>
                            <div>
                                <h3 className="text-3xl font-extrabold text-gray-900 font-poppins">{item.value}</h3>
                                <p className="text-sm text-gray-500 font-roboto">{item.label}</p>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
            
            {/* Main Content: Table */}
            <div className="max-w-7xl mx-auto">
                
                {/* Job Openings Table */}
                <motion.div
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    transition={{ delay: 0.4 }}
                    className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 h-full flex flex-col"
                >
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-4 space-y-4 sm:space-y-0">
                        <h3 className="text-2xl font-semibold text-gray-900 font-poppins"> Job Openings</h3>
                        <button 
                            onClick={() => setIsAddJobModalOpen(true)}
                            className="py-2 px-5 rounded-xl bg-indigo-600 text-white flex items-center justify-center text-sm font-medium shadow-md shadow-indigo-300 hover:bg-indigo-700 transition-all transform hover:scale-[1.02]"
                        >
                            <PlusIcon className="h-5 w-5 mr-2" />
                            Post New Job
                        </button>
                    </div>
                    
                    <div className="relative mb-6">
                        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search jobs by title, department, or status..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
                        />
                    </div>
                    
                    <div className="overflow-x-auto flex-1 border border-gray-100 rounded-xl">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50" style={{ background: "linear-gradient(180deg, #0F4C81 0%, #1976D2 45%, #42A5F5 100%)" }}>
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider font-poppins">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider font-poppins">REQ ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider font-poppins">Job Title</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider font-poppins">Department</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider font-poppins">Positions</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider font-poppins">Status</th>
                                    <th className="relative px-6 py-3"><span className="sr-only">View</span></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100 font-roboto">
                                <AnimatePresence initial={false}>
                                    {filteredJobs.length > 0 ? (
                                        filteredJobs.map((job) => (
                                            <motion.tr
                                                key={job.id}
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.3 }}
                                                className="hover:bg-indigo-50/50 cursor-pointer"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.date}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <button 
                                                        onClick={() => handleReqIdClick(job.reqId)}
                                                        className="text-indigo-600 hover:text-indigo-900 hover:underline transition-colors"
                                                    >
                                                        {job.reqId}
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{job.title}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.department}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">{job.positions}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <select
                                                        value={job.status}
                                                        onChange={(e) => handleStatusChange(job.id, e.target.value)}
                                                        className={`px-3 py-1 text-xs leading-5 font-medium rounded-full ring-1 ring-inset border-0 focus:ring-2 focus:ring-indigo-500 ${getStatusColor(job.status)}`}
                                                    >
                                                        <option value="In Progress">In Progress</option>
                                                        <option value="Hold">Hold</option>
                                                        <option value="Closed">Closed</option>
                                                    </select>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex items-center justify-end space-x-2">
                                                        <button 
                                                            onClick={() => handleJobDetails(job)}
                                                            className="text-indigo-600 hover:text-indigo-900 flex items-center group transition-colors"
                                                        >
                                                            Details <ChevronRightIcon className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDeleteJob(job.id)}
                                                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="py-10 text-center text-gray-500 text-base">
                                                No matching job openings found. Try adjusting your search.
                                            </td>
                                        </tr>
                                    )}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            </div>
            
            <AddJobModal
                isOpen={isAddJobModalOpen}
                onClose={() => setIsAddJobModalOpen(false)}
                onSave={handleAddJob}
            />
        </div>
    );
};

export default RecruitmentDashboard;